import { ChangeDetectionStrategy, Component, effect, inject, input, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { SharedMaterialUIModule } from '@shared/modules/shared-material-ui.module';
import { PortalResponse, UserRoleResponse } from '@core/models/common.model';
import { ClientResponse } from '@features/client/client.model';
import { SearchableLookupOption, SearchableLookupSelectComponent } from '@shared/components/searchable-lookup-select/searchable-lookup-select.component';
import { UpdateUserProfileRequest, UpdateUserRequest, UserResponse } from '../../user.model';
import { nullIfBlank } from '@shared/utils/payload-utils';

@Component({
  selector: 'update-user',
  imports: [ReactiveFormsModule, SearchableLookupSelectComponent, ...SharedMaterialUIModule],
  templateUrl: './update-user.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: ``,
})
export class UpdateUserComponent {
  private readonly formBuilder = inject(FormBuilder);

  readonly title = input('Update user');
  readonly subtitle = input('Edit first and last name fields.');
  readonly submitLabel = input('Save changes');
  readonly busy = input(false);
  readonly includeAssignments = input(false);
  readonly clients = input<ClientResponse[]>([]);
  readonly portals = input<PortalResponse[]>([]);
  readonly userRoles = input<UserRoleResponse[]>([]);
  readonly affiliateOptions = input<SearchableLookupOption[]>([]);
  readonly affiliateOptionsLoading = input(false);
  readonly initialValue = input<UserResponse | null>(null);
  readonly submitted = output<UpdateUserRequest | UpdateUserProfileRequest>();
  readonly affiliateSearchChanged = output<string>();

  readonly updateForm = this.formBuilder.nonNullable.group({
    firstName: ['', Validators.required],
    lastName: ['', Validators.required],
    portalId: [''],
    userRoleId: [''],
    clientId: [''],
    affiliateId: [''],
  });

  private readonly selectedPortalId = toSignal(this.updateForm.controls.portalId.valueChanges, {
    initialValue: this.updateForm.controls.portalId.value,
  });

  protected isClientPortal(): boolean {
    return this.portalName(this.selectedPortalId()) === 'client';
  }

  protected isAffiliatePortal(): boolean {
    return this.portalName(this.selectedPortalId()) === 'affiliate';
  }

  private portalName(portalId: string): string {
    return this.portals().find((item) => item.id === portalId)?.name?.toLowerCase() ?? '';
  }

  constructor() {
    effect(() => {
      if (!this.includeAssignments()) {
        return;
      }

      const portalName = this.portalName(this.selectedPortalId());

      if (portalName !== 'client') {
        this.updateForm.controls.clientId.setValue('', { emitEvent: false });
      }

      if (portalName !== 'affiliate') {
        this.updateForm.controls.affiliateId.setValue('', { emitEvent: false });
      }
    });

    effect(() => {
      const portalControl = this.updateForm.controls.portalId;
      const userRoleControl = this.updateForm.controls.userRoleId;
      if (this.includeAssignments()) {
        portalControl.setValidators([Validators.required]);
        userRoleControl.setValidators([Validators.required]);
      } else {
        portalControl.clearValidators();
        userRoleControl.clearValidators();
      }

      portalControl.updateValueAndValidity({ emitEvent: false });
      userRoleControl.updateValueAndValidity({ emitEvent: false });
    });

    effect(() => {
      const value = this.initialValue();
      if (!value) {
        return;
      }

      this.updateForm.reset({
        firstName: value.firstName,
        lastName: value.lastName,
        portalId: value.portalId,
        userRoleId: value.userRoleId,
        clientId: value.clientId ?? '',
        affiliateId: value.affiliateId ?? '',
      });
    });
  }

  protected submit(): void {
    if (this.updateForm.invalid) {
      this.updateForm.markAllAsTouched();
      return;
    }

    const value = this.updateForm.getRawValue();
    if (this.includeAssignments()) {
      this.submitted.emit({
        firstName: value.firstName,
        lastName: value.lastName,
        portalId: value.portalId,
        userRoleId: value.userRoleId,
        clientId: nullIfBlank(value.clientId),
        affiliateId: nullIfBlank(value.affiliateId),
      });
      return;
    }

    this.submitted.emit({
      firstName: value.firstName,
      lastName: value.lastName,
    });
  }
}
